import {useToast} from "@chakra-ui/react";
import {getUploadLink} from "../services/projectService";
import {BlockBlobClient} from "@azure/storage-blob";
import {getFileBaseName} from "../utils/utils.ts";
import iso6391 from "iso-639-1";
import {ALLOWED_TYPES} from "../models/constants/constants";
import {useProjectsStore} from "../store/useProjectsStore";
import {useUploadStore} from "../store/useUploadStore";
import {ICreateNewProjectState} from "../reducers/createNewProjectReducer.ts";
import {INewProject} from "../models/interfaces/newProject.interface.ts";
import {IUploadLink} from "../models/interfaces/uploadLink.interface.ts";
import useUserStore from "../store/useUserStore.ts";
import {useCreditsStore} from "../store/useCreditsStore.ts";
import {isInsufficientCreditsError} from "../utils/creditsErrors.ts";
import {
  reportUploadLifecycle,
  type UploadLifecycleStage,
} from "../services/uploadLifecycleService.ts";

// Parallel Put Block upload: a single-request PUT is capped by one TCP stream
// (~10 Mbps to the storage region => 20 min for 1.3 GB). Do not regress to a single PUT.
const UPLOAD_BLOCK_SIZE_BYTES: number = 8 * 1024 * 1024;
const UPLOAD_CONCURRENCY: number = 8;
const lifecycleReportChains = new Map<string, Promise<void>>();

const reportLifecycleBestEffort = (
  uploadId: string,
  lifecycleStage: UploadLifecycleStage,
): void => {
  const previousReport = lifecycleReportChains.get(uploadId) ?? Promise.resolve();
  const currentReport = previousReport
    .then(() => reportUploadLifecycle({uploadId, lifecycleStage}))
    .catch(() => undefined);
  lifecycleReportChains.set(uploadId, currentReport);
  void currentReport.then(() => {
    if (lifecycleReportChains.get(uploadId) === currentReport) {
      lifecycleReportChains.delete(uploadId);
    }
  });
};

export const useProjectUploader = () => {
  const {addNewProjectCreationConfiguration} = useUserStore();
  const toast = useToast();
  const {createProject} = useProjectsStore();
  const uploads = useUploadStore((state) => state.uploads);
  const beginUpload = useUploadStore((state) => state.beginUpload);
  const updateUploadProgress = useUploadStore((state) => state.updateUploadProgress);
  const completeUpload = useUploadStore((state) => state.completeUpload);
  const failUpload = useUploadStore((state) => state.failUpload);
  const dismissUpload = useUploadStore((state) => state.dismissUpload);

  const triggerUpload: (state: ICreateNewProjectState) => Promise<void> = async (state: ICreateNewProjectState): Promise<void> => {
    const {
      attachedFiles,
      customConfigurationName,
      shouldSaveTemplate,
      subtitles,
      fileNameToSeriesNameRelation,
    } = state;

    try {
      // Payload lock: exactly these four keys. `dialogueListConfig` and `audioDescriptions` stay
      // defined on the DTO for existing projects but are never emitted by the create flow.
      const configuration: INewProject["features"] = {
        sourceTranscriptionConfig: {
          ...Object.values(subtitles.sourceLanguage)[0],
          keepSongs: subtitles.keepSongs ?? false,
        },
        subtitles: subtitles.isTranscribeOnly
          ? {}
          : Object.fromEntries(
            Object.entries(subtitles.targetLanguages).map(([lang, config]) => [
              iso6391.getCode(lang),
              config,
            ])
          ),
        shotChangeSync: subtitles.shotChangeSync ?? false,
        dubbingScript: subtitles.dubbingScript ?? false,
      }
      const sourceLanguageCode: string = iso6391.getCode(Object.keys(subtitles.sourceLanguage)[0])
      const templateName: string = customConfigurationName.trim();
      if (shouldSaveTemplate && templateName !== "") {
        // Saving the preset is a convenience: never let it abort the project the user asked for.
        try {
          await addNewProjectCreationConfiguration(templateName, sourceLanguageCode, configuration);
          toast({
            position: "bottom-left",
            title: "Configuration created",
            description: "Your custom configuration has been created successfully.",
            status: "success",
            duration: 2000,
            isClosable: true,
          });
        } catch (error) {
          console.error("Saving the project configuration failed:", error);
          toast({
            position: "bottom-left",
            title: "Configuration not saved",
            description: "We could not save your configuration, but your project is still being created.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      }
      const uploadLinkResponse: IUploadLink[] = await getUploadLink(
        ALLOWED_TYPES[attachedFiles[0].type],
        attachedFiles.length
      );

      attachedFiles.forEach((file: File, index: number) => {
        beginUpload(uploadLinkResponse[index].uploadId, file.name);
      });

      const uploadPromises: Promise<void>[] = attachedFiles.map(async (file: File, i: number) => {
        const {uploadUrl, uploadId} = uploadLinkResponse[i];
        const blobClient = new BlockBlobClient(`${uploadUrl.url}?${uploadUrl.sasToken}`);

        reportLifecycleBestEffort(uploadId, "started");
        try {
          await blobClient.uploadData(file, {
            blockSize: UPLOAD_BLOCK_SIZE_BYTES,
            maxSingleShotSize: UPLOAD_BLOCK_SIZE_BYTES,
            concurrency: UPLOAD_CONCURRENCY,
            blobHTTPHeaders: {blobContentType: file.type},
            onProgress: ({loadedBytes}) => {
              const percentage: number = Math.round((loadedBytes / (file.size || 1)) * 100);
              updateUploadProgress(uploadId, percentage);
            },
          });
        } catch (error) {
          reportLifecycleBestEffort(uploadId, "failed");
          const message = error instanceof Error && error.message.length > 0
            ? error.message
            : "Something went wrong. Please try again.";
          failUpload(uploadId, message);
          toast({
            position: "bottom-left",
            title: "Upload failed",
            description: message,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
          return;
        }

        reportLifecycleBestEffort(uploadId, "blob_completed");
        updateUploadProgress(uploadId, 100);

        const newProject: INewProject = {
          name: getFileBaseName(file.name),
          seriesName: fileNameToSeriesNameRelation[file.name] || undefined,
          uploadId: uploadId,
          sourceLanguage: sourceLanguageCode,
          durationSeconds: state.durationSecondsByFileName[file.name],
          features: configuration,
        };

        try {
          await createProject(newProject);
          completeUpload(uploadId);
          dismissUpload(uploadId);
          const workspaceId = useUserStore.getState().user?.workspaceId;
          if (workspaceId) void useCreditsStore.getState().fetchBalance(workspaceId);
        } catch (error) {
          // Residual race: the dialog closed at submit, and someone spent the balance during the upload.
          if (isInsufficientCreditsError(error) && error.response) {
            useCreditsStore.getState().openNotEnough({
              requiredCredits: error.response.data.requiredCredits,
              availableCredits: error.response.data.availableCredits,
            });
            failUpload(uploadId, "Not enough credits");
            return;
          }
          const message = error instanceof Error && error.message.length > 0
            ? error.message
            : "Something went wrong. Please try again.";
          failUpload(uploadId, message);
          toast({
            position: "bottom-left",
            title: "Upload failed",
            description: message,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      });

      await Promise.all(uploadPromises);
    } catch (error) {
      console.error("Upload failed:", error);
      toast({
        position: "bottom-left",
        title: "Upload failed",
        description: "Something went wrong. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return {uploads, triggerUpload};
};
